import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";
import { API_ENDPOINT } from "../../config";
import {
  CreateQuestionDto,
  GetQuestionDto,
  Question,
  ListResponse,
  UpdateQuestionDto,
} from "../../interfaces";
import { toastMessage } from "../../utils/toastError";

/* --------------------------------
 * Types for Question Reports
 * -------------------------------- */
export type QuestionReportStatus = "PENDING" | "RESOLVED" | "DISCARDED";

export interface QuestionReport {
  report_id: string;
  question_id: string;
  student_id: string;
  student_name: string;
  reason: string;
  description: string;
  admin_comment?: string;
  admin_resolution_reason?: string;
  status: QuestionReportStatus;
  reported_at: string;
  resolved_at?: string;
}

/* --------------------------------
 * Admin grouped response
 * -------------------------------- */
export interface GroupedQuestionReport {
  question_id: string;
  question: Question;
  total_reports: number;
  latest_reported_at: string;
  reports: QuestionReport[];
}

/* --------------------------------
 * Types for Question Analytics
 * -------------------------------- */
export interface QuestionAnalytics {
  summary: {
    total: number;
    today: number;
    thisMonth: number;
    thisYear: number;
  };

  yearly: {
    year: number;
    count: number;
  }[];

  monthly: {
    month: string;
    month_index: number;
    count: number;
  }[];

  daily: {
    date: string;
    count: number;
  }[];

  subjectWise: {
    subject_id: string;
    subject_name: string;
    count: number;
  }[];

  chapterWise: {
    chapter_id: string;
    chapter_name: string;
    subject_name: string;
    count: number;
  }[];

  difficultyWise: {
    difficulty_level: string;
    count: number;
  }[];

  creatorWise: {
    admin_id: string;
    admin_name: string;
    count: number;
  }[];

  creatorYearly: {
    admin_id: string;
    admin_name: string;
    year: number;
    count: number;
  }[];

  creatorMonthly: {
    admin_id: string;
    admin_name: string;
    month: string;
    month_index: number;
    count: number;
  }[];
}

export const questionApi = createApi({
  reducerPath: "questionApi",
  baseQuery: fetchBaseQuery({
    baseUrl: API_ENDPOINT + "/question",
    credentials: "include",
  }),
  endpoints: (build) => ({
    /* ---------------- QUESTIONS ---------------- */

    getQuestion: build.query<Question, string>({
      query: (id) => `/${id}`,
    }),

    getQuestions: build.query<ListResponse<Question>, GetQuestionDto>({
      query: (params) => ({
        url: "/",
        method: "GET",
        params,
      }),
    }),

    getQuestionStats: build.query<
      { questions: number; subjects: number; chapters: number; reported_questions: number },
      void
    >({
      query: () => "/stats",
    }),

    generateQuestionsPdf: build.mutation<
      Blob,
      { heading: string; questions: any[] }
    >({
      query: (body) => ({
        url: "/pdf",
        method: "POST",
        body,
        responseHandler: async (response) => response.blob(),
      }),
    }),

    createQuestion: build.mutation<Question, CreateQuestionDto>({
      query: (payload) => ({
        url: "/",
        method: "POST",
        body: payload,
      }),
      transformResponse(res: Question) {
        toastMessage("success", "Successfully created");
        return res;
      },
      transformErrorResponse(err: any) {
        toastMessage("error", err.data);
        return Promise.reject(err.data);
      },
    }),

    updateQuestion: build.mutation<Question, UpdateQuestionDto>({
      query: (payload) => ({
        url: "/",
        method: "PUT",
        body: payload,
      }),
      transformResponse(res: Question) {
        toastMessage("success", "Successfully updated");
        return res;
      },
      transformErrorResponse(err: any) {
        toastMessage("error", err.data);
        return Promise.reject(err.data);
      },
    }),

    getQuestionHistory: build.query<any, string>({
      query: (questionId) => `/questionhistory/${questionId}`,
    }),

    deleteQuestion: build.mutation<any, string[]>({
      query: (ids) => ({
        url: "/",
        method: "DELETE",
        body: { ids },
      }),
      transformResponse(res) {
        toastMessage("success", "Successfully deleted");
        return res;
      },
      transformErrorResponse(err: any) {
        toastMessage("error", err.data);
        return Promise.reject(err.data);
      },
    }),

    /* ---------------- QUESTION REPORTS (ADMIN) ---------------- */

    // 🔥 GROUPED BY QUESTION
    getReportedQuestionsForAdmin: build.query<
      { total_questions: number; questions: GroupedQuestionReport[] },
      { status?: QuestionReportStatus }
    >({
      query: (params) => ({
        url: "/report",
        method: "GET",
        params,
      }),
    }),

    // Accordion view → reports for one question
    getReportsByQuestion: build.query<
      { question_id: string; total_reports: number; reports: QuestionReport[] },
      string
    >({
      query: (questionId) => `/report/question/${questionId}`,
    }),

    // 🔥 Resolve ALL reports for a question
    resolveReportsByQuestion: build.mutation<
      void,
      {
        questionId: string;
        admin_comment: string;
        admin_resolution_reason: string;
      }
    >({
      query: ({ questionId, ...body }) => ({
        url: `/report/question/${questionId}/resolve`,
        method: "PATCH",
        body,
      }),
      transformResponse() {
        toastMessage("success", "Question fixed and all reports resolved");
      },
      transformErrorResponse(err: any) {
        toastMessage("error", err.data);
        return Promise.reject(err.data);
      },
    }),

    /* ---------------- QUESTION ANALYTICS (ADMIN) ---------------- */

    getQuestionAnalytics: build.query<QuestionAnalytics, void>({
      query: () => "/analytics",
    }),
  }),
});

export const {
  /* Questions */
  useCreateQuestionMutation,
  useGetQuestionsQuery,
  useGetQuestionStatsQuery,
  useGetQuestionQuery,
  useLazyGetQuestionQuery,
  useUpdateQuestionMutation,
  useDeleteQuestionMutation,
  useGenerateQuestionsPdfMutation,
  useLazyGetQuestionHistoryQuery,

  /* Reports */
  useGetReportedQuestionsForAdminQuery,
  useGetReportsByQuestionQuery,
  useResolveReportsByQuestionMutation,

  /* Analytics */
  useGetQuestionAnalyticsQuery,
} = questionApi;
